import { Link, useLocation } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'

const navItems = [
  { path: '/', icon: 'dashboard', label: 'Dashboard' },
  { path: '/quick-start', icon: 'rocket_launch', label: 'Quick Start Guide' },
  { path: '/projects', icon: 'folder_open', label: 'My Projects' },
  { path: '/gallery', icon: 'photo_library', label: 'Gallery' },
  { path: '/styles', icon: 'style', label: 'Style References' },
  { path: '/subscription', icon: 'card_membership', label: 'Subscription' },
  { path: '/settings', icon: 'settings', label: 'Settings' },
]

export function Sidebar() {
  const location = useLocation()
  const { user } = useAuthStore()

  // Use availableCredits for display (subscription + bonus - reserved)
  const availableCredits = user?.availableCredits ?? user?.credits ?? 0
  const maxCredits = user?.maxCredits ?? (user?.monthlyCredits ?? 10) * 3
  const creditsPercentage = user
    ? Math.round((availableCredits / maxCredits) * 100)
    : 0

  return (
    <aside className="w-72 bg-surface border-r border-border-color flex-col justify-between p-6 hidden md:flex shrink-0 z-20 shadow-sm">
      <div className="flex flex-col gap-8">
        {/* Logo */}
        <div className="flex items-center gap-3 px-2">
          <img src="/logo.png" alt="Kalypso AI" className="size-10 rounded-xl object-cover" />
          <h1 className="text-2xl font-bold tracking-tight text-primary-dark">Kalypso AI</h1>
        </div>

        {/* Navigation */}
        <nav className="flex flex-col gap-1.5">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-xl transition-colors relative
                  ${isActive
                    ? 'bg-accent/50 text-primary-dark font-medium nav-item-active'
                    : 'text-text-muted hover:bg-surface-soft hover:text-text-main'
                  }
                `}
              >
                <span className={`material-symbols-outlined ${isActive ? 'filled' : ''}`}>
                  {item.icon}
                </span>
                <span className="text-sm">{item.label}</span>
              </Link>
            )
          })}
        </nav>
      </div>

      <div className="flex flex-col gap-6">
        {/* Credits Section */}
        <div className="bg-gradient-to-br from-surface-soft to-accent/30 p-5 rounded-xl flex flex-col gap-3 border border-primary/10 shadow-soft">
          <div className="flex justify-between items-center text-xs font-semibold text-text-muted uppercase tracking-wider">
            <span>Available Credits</span>
            <span className="text-primary-dark font-bold">{availableCredits}</span>
          </div>
          <div className="w-full bg-white/60 rounded-full h-2.5 overflow-hidden">
            <div
              className="bg-gradient-to-r from-primary to-primary-dark h-2.5 rounded-full transition-all duration-500"
              style={{ width: `${Math.min(creditsPercentage, 100)}%` }}
            />
          </div>
          {user?.bonusCredits && user.bonusCredits > 0 && (
            <div className="text-[10px] text-amber-600 flex items-center gap-1">
              <span className="material-symbols-outlined text-xs">shopping_bag</span>
              Includes {user.bonusCredits} from Credit Packs
            </div>
          )}
          <Link
            to="/subscription"
            className="text-xs text-primary-dark font-bold text-left hover:underline flex items-center gap-1 mt-1 group"
          >
            Upgrade Plan
            <span className="material-symbols-outlined text-[14px] group-hover:translate-x-0.5 transition-transform">arrow_forward</span>
          </Link>
        </div>

        {/* User Profile */}
        <div className="flex items-center gap-3 px-2 pt-2 border-t border-border-color">
          {user?.avatarUrl ? (
            <div
              className="bg-center bg-no-repeat bg-cover rounded-full size-10 border-2 border-white shadow-sm"
              style={{ backgroundImage: `url("${user.avatarUrl}")` }}
            />
          ) : (
            <div className="size-10 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="material-symbols-outlined text-primary">person</span>
            </div>
          )}
          <div className="flex flex-col">
            <p className="text-sm font-bold text-text-main">{user?.name || 'User'}</p>
            <p className="text-xs text-text-muted">{user?.subscriptionTier || 'Free'} Plan</p>
          </div>
        </div>
      </div>
    </aside>
  )
}
